import { useEffect, useState, type ReactNode } from "react";

import { Button } from "../components/Button";
import { Badge } from "../components/Display";
import { Icon, type IconName } from "../components/Icon";
import { Sheet, SheetContent, SheetTitle, Tooltip, TooltipProvider } from "../components/Overlays";
import { cn } from "../lib/cn";

export interface NavItem {
  id: string;
  label: string;
  icon: IconName;
  href: string;
  active?: boolean;
  badge?: string | number;
}

export interface NavGroup {
  id: string;
  /** Group heading; omit for the top group (e.g. Dashboard). */
  label?: string;
  items: NavItem[];
}

export interface RenderLinkProps {
  href: string;
  className: string;
  children: ReactNode;
  "aria-current"?: "page";
  onClick?: () => void;
}

export interface PanelShellLabels {
  openMenu: string;
  collapse: string;
  expand: string;
  skipToContent: string;
  navigation: string;
}

export interface PanelShellProps {
  /** Logo for the dark sidebar (use tone="light"). */
  brand: ReactNode;
  /** Smaller brand for the collapsed sidebar. */
  brandCollapsed?: ReactNode;
  navGroups: NavGroup[];
  /** Router adapter, e.g. `(p) => <Link to={p.href} …>{p.children}</Link>` (keeps the UI kit router-free). */
  renderLink: (props: RenderLinkProps) => ReactNode;
  /** Left side of the top bar (e.g. global search). */
  topbarStart?: ReactNode;
  /** Right side of the top bar (language, notifications, user menu). */
  topbarEnd?: ReactNode;
  children: ReactNode;
  labels?: Partial<PanelShellLabels>;
  /** localStorage key for the collapsed state (one per panel). */
  storageKey?: string;
}

const defaultLabels: PanelShellLabels = {
  openMenu: "Open menu",
  collapse: "Collapse sidebar",
  expand: "Expand sidebar",
  skipToContent: "Skip to content",
  navigation: "Main navigation",
};

function readCollapsed(key: string): boolean {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false; // storage blocked (private mode): default expanded
  }
}

function SidebarNav({
  groups,
  collapsed,
  renderLink,
  onNavigate,
}: {
  groups: NavGroup[];
  collapsed: boolean;
  renderLink: PanelShellProps["renderLink"];
  onNavigate?: () => void;
}) {
  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <div key={group.id}>
          {group.label && !collapsed && (
            <p className="mb-2 px-3 text-[0.68rem] font-semibold tracking-[0.14em] text-ink-foreground/45 uppercase">
              {group.label}
            </p>
          )}
          <ul className="space-y-1">
            {group.items.map((item) => {
              const link = renderLink({
                href: item.href,
                "aria-current": item.active ? "page" : undefined,
                onClick: onNavigate,
                className: cn(
                  "flex items-center gap-3 rounded-full px-3 py-2.5 text-sm font-medium transition-colors",
                  collapsed && "justify-center px-0",
                  item.active
                    ? "bg-brand text-brand-foreground shadow-card"
                    : "text-ink-foreground/75 hover:bg-ink-foreground/8 hover:text-ink-foreground",
                ),
                children: (
                  <>
                    <Icon name={item.icon} size={18} />
                    {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
                    {!collapsed && item.badge != null && (
                      <Badge tone={item.active ? "ink" : "brand"} className="px-2">
                        {item.badge}
                      </Badge>
                    )}
                  </>
                ),
              });
              return (
                <li key={item.id}>
                  {collapsed ? (
                    <Tooltip content={item.label} side="right">
                      <span className="block">{link}</span>
                    </Tooltip>
                  ) : (
                    link
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

/** The admin/vendor panel frame: dark sidebar with yellow active pill + white top bar. */
export function PanelShell({
  brand,
  brandCollapsed,
  navGroups,
  renderLink,
  topbarStart,
  topbarEnd,
  children,
  labels: labelOverrides,
  storageKey = "motolynk.sidebar",
}: PanelShellProps) {
  const labels = { ...defaultLabels, ...labelOverrides };
  const [collapsed, setCollapsed] = useState(() => readCollapsed(storageKey));
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, collapsed ? "1" : "0");
    } catch {
      /* storage unavailable: preference just isn't remembered */
    }
  }, [collapsed, storageKey]);

  return (
    <TooltipProvider>
      <div className="flex min-h-dvh bg-canvas">
        <a
          href="#main"
          className="sr-only z-50 rounded-full bg-brand px-4 py-2 font-semibold text-brand-foreground focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
        >
          {labels.skipToContent}
        </a>

        {/* Desktop sidebar */}
        <aside
          className={cn(
            "sticky top-0 hidden h-dvh shrink-0 flex-col bg-ink text-ink-foreground transition-[width] duration-200 lg:flex",
            collapsed ? "w-20" : "w-68",
          )}
        >
          <div className={cn("flex h-16 items-center px-5", collapsed && "justify-center px-0")}>
            {collapsed ? (brandCollapsed ?? brand) : brand}
          </div>
          <nav aria-label={labels.navigation} className="flex-1 overflow-y-auto px-3 py-4">
            <SidebarNav groups={navGroups} collapsed={collapsed} renderLink={renderLink} />
          </nav>
          <div className={cn("border-t border-ink-border p-3", collapsed && "flex justify-center")}>
            <Button
              variant="ghost"
              size={collapsed ? "icon" : "sm"}
              onClick={() => setCollapsed((c) => !c)}
              className="text-ink-foreground/70 hover:bg-ink-foreground/8 hover:text-ink-foreground"
              aria-label={collapsed ? labels.expand : labels.collapse}
            >
              <Icon name={collapsed ? "panel-open" : "panel-close"} size={18} />
              {!collapsed && labels.collapse}
            </Button>
          </div>
        </aside>

        {/* Mobile sidebar */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="w-72 border-0 bg-ink text-ink-foreground">
            <SheetTitle className="sr-only">{labels.navigation}</SheetTitle>
            <div className="flex h-16 items-center px-5">{brand}</div>
            <nav aria-label={labels.navigation} className="flex-1 overflow-y-auto px-3 py-4">
              <SidebarNav
                groups={navGroups}
                collapsed={false}
                renderLink={renderLink}
                onNavigate={() => setMobileOpen(false)}
              />
            </nav>
          </SheetContent>
        </Sheet>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-surface/95 px-4 backdrop-blur sm:px-6">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              aria-label={labels.openMenu}
              onClick={() => setMobileOpen(true)}
            >
              <Icon name="menu" />
            </Button>
            <div className="min-w-0 flex-1">{topbarStart}</div>
            <div className="flex items-center gap-1 sm:gap-2">{topbarEnd}</div>
          </header>
          <main
            id="main"
            className="mx-auto w-full max-w-[90rem] flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8"
          >
            {children}
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
